import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Input,
  Label,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import { ThreeDots } from "react-loader-spinner";

import { resolveMachineRouting } from "../../services/capabilitiesService";
import { postChangeset } from "./shared/captureDeviceService";
import buildChangeset from "./shared/changeset";
import { MAX_IN_FLIGHT, captureFrame, deviceTarget, runPool } from "./shared/dashcamScan";
import expandHostRange, { expandToken, hasPlaceholder } from "./shared/hostRange";
import buildDiscoveredCamera, { expandSuffix } from "./shared/discoveredCamera";
import ProbeFrame from "./shared/ProbeFrame";
import resolveDeviceServerName from "./shared/resolveServerName";
import InferenceMachineAutocomplete from "./TreePrototype/forms/InferenceMachineAutocomplete";
import { tempPublicId } from "./TreePrototype/treeReducer";

/**
 * Provision a subnet of IP cameras: probe a host range, keep what answered.
 *
 * The camera-model replacement for a legacy `VideoRecorder` whose `device_ip` carried a `{}` and
 * stood for N IP cameras (3273 such rows are live). `CaptureDevice.host` forbids the placeholder
 * (`capturedevice_host_no_curly_placeholder`) and one IP camera is one device, so the range is
 * expanded client-side and only literal hosts are ever sent.
 *
 * Probe first, create after: each host gets one dashcam capture on slot 1, and only the hosts that
 * came back with an image can be created. That inverts the blind flow this replaces — creating rows
 * for hosts nobody answered for left the operator deleting devices afterwards to find out which were
 * real. Since a frame on slot 1 *is* the evidence a camera is there, the batch creates the device
 * **and** its camera and view: re-scanning afterwards would only rediscover what is already on screen.
 *
 * The probe goes through `POST /dashcam/screenshot-async` with the typed connection fields only, so no
 * value can be inherited from a device the probe is not describing.
 *
 * The one thing the data model cannot supply is the routing machine: a store with no camera has none
 * (the Machine↔Store link runs through legacy `Channel`, and `ForwarderMachine` covers 8 production
 * stores), so the operator picks it. When the tree does resolve one, that is the default.
 */

/** Fields shared by every device of the batch, plus their form default. */
const DEFAULT_FORM = {
  name: "",
  brand: "",
  transport: "RTSP",
  host: "",
  rtsp_port: "554",
  api_port: "",
  rtsp_port_redirected: "",
  api_port_redirected: "",
  username: "",
  password: "",
  // Fixed default — not exposed in the form.
  caching: "3",
  rtsp_url_suffix: "",
  protocol: "TCP",
  stream_quality: "SUB",
};

/** Fields a probe may not leave without: dashcam dials with them, and `rtsp_port` is non-nullable. */
const REQUIRED_FIELDS = ["username", "password", "rtsp_port"];

/**
 * The only brand kind this flow can honestly offer.
 *
 * `IP` is the kind whose URL is built from `rtsp_url_suffix`, and it is
 * the shape this form creates: one host, one device, one camera on slot 1. A branded DVR (HIK, DAHUA)
 * serves N channels behind one address — those are discovered per device, after the row exists.
 */
const PROBE_BRAND_KIND = "IP";
/** Preferred IP brand: the one every mirrored IP camera in production carries. */
const DEFAULT_IP_BRAND_NAME = "GENERIC_IP";

const NUMERIC_FIELDS = ["rtsp_port", "api_port", "rtsp_port_redirected", "api_port_redirected", "caching"];

/** How many hosts one probe run may cover: each one costs a capture. */
const MAX_PROBE_HOSTS = 50;
/** One slot: an IP camera is one device, one camera, slot 1. */
const PROBE_SLOT = 1;

/**
 * `""` → null for a nullable integer column, so an untouched optional port is not sent as a string.
 *
 * @param {string} value - The raw input value.
 * @returns {number|null} The number, or null when empty/unreadable.
 */
function toNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Reduce an error body to a string safe to render — the two shapes this API produces: a bare string
 * on a 400, `{detail: …}` from DRF. Putting a dict into state kills the page ("Objects are not valid
 * as a React child").
 *
 * @param {*} body - The candidate message.
 * @param {string} fallback - What to show when nothing usable can be extracted.
 * @returns {string} A displayable string.
 */
function errorText(body, fallback) {
  if (typeof body === "string" && body.trim()) return body;
  const detail = body?.detail ?? body?.message;
  return typeof detail === "string" && detail.trim() ? detail : fallback;
}

/**
 * The reason the changeset refused the batch, attributed to the host that caused it.
 *
 * `ChangesetView` answers a 422 with `{"errors": [{op_id, code, field?, message}]}` and no top-level
 * message. The batch is atomic and stops at the first failure, so one bad host loses every other:
 * naming it is the difference between a fixable error and an opaque one. `field` is surfaced because
 * a refusal on a field shared by the batch is not one host's fault, and every ref of the batch —
 * device, camera, view — maps back to a host, since the op that trips is often a child.
 *
 * @param {object} error - The rejected axios error.
 * @param {Array<object>} ops - The ops that were sent.
 * @param {Map<string, string>} hostByRef - Every `client_ref` of the batch → its device's host.
 * @returns {string} A displayable reason.
 */
function changesetErrorMessage(error, ops, hostByRef) {
  const data = error?.response?.data;
  const first = Array.isArray(data?.errors) ? data.errors[0] : null;
  const detail = errorText(first?.message ?? data, "Failed to create the capture devices.");
  const message = first?.field ? `on field "${first.field}": ${detail}` : detail;
  const culprit = (ops || []).find((op) => op.op_id === first?.op_id);
  const host = hostByRef.get(culprit?.client_ref);
  return host == null ? message : `${host} was refused, so nothing was created: ${message}`;
}

export default function BulkCaptureDeviceForm({ storeId, brands, tree, machineCache, onCreated, onClose }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [probes, setProbes] = useState({});
  const [probing, setProbing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [failure, setFailure] = useState(null);
  // Set once a batch landed; the form stays open so the next range reuses the same connection fields.
  const [created, setCreated] = useState(0);
  // The machine the operator picked, when the tree resolves none — or to override the one it did.
  const [pickedMachine, setPickedMachine] = useState({ id: null, hostname: null });
  // The brand is imposed; this only opens the (rare) choice between the seeded IP brands.
  const [brandOpen, setBrandOpen] = useState(false);

  // `onCreated` closes this form, and a promise continuation is not batched in React 17: the unmount
  // lands before the `finally`. Same guard as `DeviceDiscoveryPanel`.
  /**
   * Which run an async answer belongs to.
   *
   * Editing a connection field clears the probes, but a capture already in flight still resolves
   * afterwards and would reinstate a tile — a frame obtained with the old
   * credentials, approved against the new ones. Every continuation checks its token first.
   */
  const runToken = useRef(0);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const { hosts, error: rangeError } = useMemo(() => expandHostRange(form.host), [form.host]);

  /**
   * Hosts already in this store's tree, port-agnostic.
   *
   * `CaptureDevice` has no uniqueness constraint beyond the `{}` check, so a duplicate would be
   * created silently with nothing to catch afterwards. Such a host leaves the run entirely: there is
   * nothing to learn from probing an address that already has its device.
   */
  const existingHosts = useMemo(() => {
    const set = new Set();
    (tree || []).forEach((device) => {
      if (device?.host) set.add(String(device.host));
    });
    return set;
  }, [tree]);

  const candidates = hosts.filter((entry) => !existingHosts.has(entry.host));
  const skipped = hosts.length - candidates.length;

  // The routing machine: the operator's pick wins, else whatever the store's cameras resolve to. The
  // resolver walks past `#<id>` placeholders, which are display-only and would corrupt the request
  // path. `null` as the device: nothing is scoped to a device here, only the store fallback applies.
  const { serverName: resolvedServer } = resolveDeviceServerName(null, tree, machineCache);
  const serverName = pickedMachine.hostname || resolvedServer;
  /**
   * The id of the machine the probe goes through, persisted on the cameras it creates.
   *
   * The picked one carries its id; a machine resolved from the tree is known by hostname only, so it
   * is looked back up in the cache. An ambiguous or absent match persists nothing rather than a guess.
   */
  const machineId = useMemo(() => {
    if (pickedMachine.id != null) return pickedMachine.id;
    const matches = Object.entries(machineCache || {}).filter(([, hostname]) => hostname === serverName);
    return matches.length === 1 ? Number(matches[0][0]) : null;
  }, [pickedMachine.id, machineCache, serverName]);
  const serverReason = serverName
    ? null
    : "Pick the inference machine to probe through — no camera in this store resolves one yet.";

  /**
   * The IP brands, and the one used unless the operator switches.
   *
   * `brand` is a non-nullable FK, so it cannot be guessed: with no IP brand in the list — a failed
   * fetch, or a changed seed — probing is refused rather than creating rows on the wrong brand.
   */
  const ipBrands = useMemo(
    () => (brands || []).filter((brand) => String(brand.kind).toUpperCase() === PROBE_BRAND_KIND),
    [brands]
  );
  const defaultBrand = ipBrands.find((brand) => brand.name === DEFAULT_IP_BRAND_NAME) || ipBrands[0] || null;
  const chosenBrand = ipBrands.find((brand) => String(brand.id) === String(form.brand)) || defaultBrand;
  const brandKind = chosenBrand?.kind || null;
  const brandError = chosenBrand
    ? null
    : "No IP brand available — the recorder-brands list did not load, so a device cannot be created.";

  /**
   * The name a device gets when the operator typed none: the mirror's own convention,
   * `f"{brand.name} @ {host}:{rtsp_port}"` (`conversion.py::_build_capture_device`), which every
   * mirrored row in production carries. A blank name is legal but leaves an unreadable tree.
   *
   * @param {string} host - The device's literal host.
   * @returns {string} The name to persist.
   */
  const deviceName = (host) => {
    const typed = expandToken(form.name, hosts.find((entry) => entry.host === host)?.token).trim();
    if (typed) return typed;
    return `${chosenBrand?.name ?? ""} @ ${host}:${form.rtsp_port}`.trim();
  };

  const hostTemplated = hasPlaceholder(form.host);
  const suffixTemplated = hasPlaceholder(form.rtsp_url_suffix);
  // A probe covers one slot, so a suffix placeholder has nothing to vary over — and the legacy form
  // treated the two templating branches as exclusive too (`ScanVideoRecorderForm.createChannels`).
  const exclusionError =
    hostTemplated && suffixTemplated
      ? "Both the host and the RTSP suffix are templated — the placeholder belongs to one or the other, not both."
      : null;
  /**
   * A brace the host range does not consume would be *persisted*.
   *
   * `CaptureDevice.name` carries no CheckConstraint and the serializer does not validate it, so the
   * API accepts `Cam {}` silently — the client is the only guard. Three ways in: a name template on a
   * literal host (nothing substitutes it), a placeholder that is not the bare `{}` (`{1}`, which
   * `split("{}")` never matches), and a stray brace.
   */
  const nameBraceError = candidates.some((entry) => hasPlaceholder(expandToken(form.name, entry.token)))
    ? "The name still contains a brace after expansion — only a bare {} on a templated host is substituted."
    : null;
  const missingFields = REQUIRED_FIELDS.filter((field) => String(form[field]).trim() === "");
  // The parent resolves the store id asynchronously and starts at null; `pickFields` keeps a null, so
  // a batch built now would 422 under the first host's name.
  const storeError = storeId == null || storeId === "" ? "The store is not resolved yet — reload the page." : null;
  const capError =
    candidates.length > MAX_PROBE_HOSTS
      ? `At most ${MAX_PROBE_HOSTS} hosts can be probed at once — each one costs its own capture.`
      : null;
  // Required: dashcam would find a path for the frame, but the camera would be saved without one.
  const suffixError = form.rtsp_url_suffix.trim()
    ? null
    : "An RTSP suffix is required — the created cameras would otherwise have no stream path.";
  const blockers = [
    rangeError,
    capError,
    exclusionError,
    nameBraceError,
    storeError,
    serverReason,
    brandError,
    suffixError,
  ];
  const canProbe =
    candidates.length > 0 &&
    blockers.every((blocker) => !blocker) &&
    missingFields.length === 0 &&
    !probing &&
    !creating;

  const rows = candidates.map((entry) => ({ ...entry, ...(probes[entry.host] || { status: "idle" }) }));
  const keptRows = rows.filter((row) => row.status === "hit" && row.kept);
  const inFlight = rows.filter((row) => row.status === "probing").length;
  const answered = rows.filter((row) => row.status === "hit").length;
  const silent = rows.filter((row) => row.status === "empty").length;

  /**
   * Editing anything the probe used invalidates its results.
   *
   * Keeping the tiles would let an operator approve a thumbnail obtained with one password or suffix
   * and then create devices carrying another.
   */
  const set = (field) => (event) => {
    // The value is read before the updater runs: React 17 still pools synthetic events, so
    // `event.target.value` inside the callback is already released and reads back empty.
    const { value } = event.target;
    // Anything still in flight now describes parameters that are gone.
    runToken.current += 1;
    setFailure(null);
    setCreated(0);
    setProbes({});
    setProbing(false);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggle = (host) => {
    setFailure(null);
    setProbes((prev) => ({ ...prev, [host]: { ...prev[host], kept: !prev[host]?.kept } }));
  };

  const fieldId = (field) => `bulk-device-${field}`;
  /**
   * A tooltip target is resolved with `querySelector`, where the dots of a host would read as class
   * selectors — hence the sanitising the checkbox ids do not need.
   */
  const tileId = (host) => `bulk-device-tile-${host.replace(/[^a-zA-Z0-9]/g, "-")}`;

  /**
   * Every connection field travels explicitly — see `REQUIRED_FIELDS`. `stream_quality` is the value
   * persisted on the created camera, so what was probed is what will stream; the suffix is the path
   * dialled, and an empty one lets dashcam discover it.
   *
   * @param {string} host - The host to probe.
   * @param {object} routing - From `resolveMachineRouting`.
   * @returns {object} A dashcam `ScreenshotRequest`.
   */
  const probeBody = (host, routing) => {
    return {
      ...deviceTarget(
        {
          host,
          rtspPort: toNumber(form.rtsp_port),
          apiPort: toNumber(form.api_port),
          username: form.username,
          password: form.password,
        },
        routing
      ),
      channel: { slot_number: PROBE_SLOT, stream_type: form.stream_quality.toLowerCase() },
      rtsp_path: expandSuffix(form.rtsp_url_suffix, PROBE_SLOT),
    };
  };

  const probeHosts = async (hostsToProbe) => {
    const token = runToken.current;
    const isStale = () => !mounted.current || runToken.current !== token;
    setProbing(true);
    setProbes((prev) => ({ ...prev, ...Object.fromEntries(hostsToProbe.map((host) => [host, { status: "queued" }])) }));
    try {
      const routing = await resolveMachineRouting(serverName);
      if (isStale()) return;
      await runPool(
        hostsToProbe,
        MAX_IN_FLIGHT,
        async (host) => {
          setProbes((prev) => ({ ...prev, [host]: { status: "probing" } }));
          const outcome = await captureFrame(probeBody(host, routing), { isStale });
          if (isStale()) return;
          // A hit starts unchecked: creating a device is not undoable from here, so the operator opts
          // each host in after looking at its frame.
          const probe =
            outcome.status === "hit"
              ? { status: "hit", url: outcome.url, operationId: outcome.operationId, kept: false }
              : { status: outcome.status, message: outcome.message };
          setProbes((prev) => ({ ...prev, [host]: probe }));
        },
        isStale
      );
    } catch (error) {
      if (!isStale()) setFailure(errorText(error?.message, "The probe failed."));
    } finally {
      if (!isStale()) setProbing(false);
    }
  };

  const runProbes = () => {
    if (!canProbe) return;
    runToken.current += 1;
    setFailure(null);
    setCreated(0);
    setProbes({});
    probeHosts(candidates.map((entry) => entry.host));
  };

  /** Stop the run without discarding what it found. Captures in flight finish server-side, unread. */
  const stopProbes = () => {
    runToken.current += 1;
    setProbing(false);
    setProbes((prev) => {
      const next = { ...prev };
      Object.entries(next).forEach(([host, probe]) => {
        if (probe.status === "probing" || probe.status === "queued") next[host] = { status: "stopped" };
      });
      return next;
    });
  };

  /**
   * Create one device per kept host, each with the camera the probe answered on and its single view.
   *
   * The probe is the evidence: a frame on slot 1 with these parameters means a camera is there, so
   * withholding the camera row would send the operator scanning for what is already on screen. Ops
   * are built by `buildChangeset` from TEMP entities against an empty server tree, so op shape,
   * `client_ref`/`parent_ref` wiring and field filtering stay in one place.
   */
  const handleCreate = async () => {
    if (keptRows.length === 0 || creating) return;
    setCreating(true);
    setFailure(null);

    const devices = keptRows.map((row) => ({
      public_id: tempPublicId("cd"),
      store: storeId,
      // A name template is expanded with the same number as its host, so a batch is not N rows
      // sharing one label; an empty field falls back to the mirror's `brand @ host:port`.
      name: deviceName(row.host),
      brand: chosenBrand.id,
      transport: form.transport,
      host: row.host,
      ...Object.fromEntries(NUMERIC_FIELDS.map((field) => [field, toNumber(form[field])])),
      username: form.username,
      password: form.password,
      cameras: [
        buildDiscoveredCamera({
          slot: PROBE_SLOT,
          suffix: form.rtsp_url_suffix,
          streamQuality: form.stream_quality,
          protocol: form.protocol,
          brandKind,
          publicId: tempPublicId("cam"),
          viewPublicId: tempPublicId("view"),
          inferenceMachine: machineId,
        }),
      ],
    }));

    const payload = buildChangeset([], devices);
    const hostByRef = new Map();
    devices.forEach((device) => {
      hostByRef.set(device.public_id, device.host);
      device.cameras.forEach((camera) => {
        hostByRef.set(camera.public_id, device.host);
        camera.views.forEach((view) => hostByRef.set(view.public_id, device.host));
      });
    });

    try {
      const answer = await postChangeset(payload);
      const publicIdByOp = new Map((answer?.applied || []).map((entry) => [entry.op_id, entry.public_id]));
      const deviceOps = payload.ops.filter((op) => op.type === "capture_device");
      const publicIds = deviceOps.map((op) => publicIdByOp.get(op.op_id)).filter(Boolean);
      if (mounted.current) {
        // The hosts are real rows now. The refreshed tree excludes them from `candidates` on its own,
        // but their tiles must not keep offering a second creation in the meantime.
        setProbes((prev) => {
          const next = { ...prev };
          keptRows.forEach((row) => delete next[row.host]);
          return next;
        });
        setCreated(deviceOps.length);
      }
      // `created` is what the server applied; the devices exist even when `applied` comes back short.
      // Called last, and the form deliberately stays open: the operator moves to the next range
      // without retyping the credentials. A host that unmounts it anyway is covered by `mounted`.
      if (onCreated) onCreated({ publicIds, created: deviceOps.length });
    } catch (error) {
      if (mounted.current) setFailure(changesetErrorMessage(error, payload.ops, hostByRef));
    } finally {
      if (mounted.current) setCreating(false);
    }
  };

  /** The one-line state of a host with no frame to show. */
  const statusLabel = (row) => {
    if (row.status === "probing") return "probing…";
    if (row.status === "queued") return "queued";
    if (row.status === "empty") return "no answer";
    if (row.status === "stopped") return "stopped before it answered";
    if (row.status === "failed") return "probe failed";
    return "not probed yet";
  };

  /**
   * The reason behind the label. Kept out of the label because these run to several lines and
   * would blow the tile out of the grid; the tile clamps it and the full text lives in the tooltip.
   */
  const statusDetail = (row) => ((row.status === "empty" || row.status === "failed") && row.message) || "";

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <CardTitle tag="h5" className="mb-0">
          Add IP cameras from a host range
        </CardTitle>
        <Button size="sm" outline color="secondary" onClick={onClose}>
          Close
        </Button>
      </CardHeader>
      <CardBody>
        <Row className="align-items-end">
          <Col md={4}>
            <Label for={fieldId("host")}>Host</Label>
            <Input
              id={fieldId("host")}
              bsSize="sm"
              value={form.host}
              invalid={Boolean(rangeError || capError)}
              onChange={set("host")}
              placeholder="192.168.4.{10-40}"
            />
          </Col>
          <Col md={3}>
            <Label for={fieldId("name")}>Name</Label>
            <Input
              id={fieldId("name")}
              bsSize="sm"
              value={form.name}
              invalid={Boolean(nameBraceError)}
              onChange={set("name")}
              placeholder="empty → BRAND @ host:port"
            />
          </Col>
          <Col md={3}>
            {/* Imposed, not asked: only the IP kind makes sense here. The select appears on demand, for
                the other seeded IP brands. */}
            <Label for={brandOpen ? fieldId("brand") : undefined}>Brand</Label>
            {brandOpen ? (
              <Input
                id={fieldId("brand")}
                type="select"
                bsSize="sm"
                className="custom-select"
                value={String(chosenBrand?.id ?? "")}
                onChange={set("brand")}
              >
                {ipBrands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </Input>
            ) : (
              <div className="d-flex align-items-center" style={{ gap: 8, height: 31 }}>
                <span>{chosenBrand?.name ?? "—"}</span>
                {ipBrands.length > 1 && (
                  <Button size="sm" outline color="secondary" onClick={() => setBrandOpen(true)}>
                    change brand
                  </Button>
                )}
              </div>
            )}
          </Col>
          <Col md={2}>
            <Label for={fieldId("transport")}>Transport</Label>
            <Input
              id={fieldId("transport")}
              type="select"
              bsSize="sm"
              className="custom-select"
              value={form.transport}
              onChange={set("transport")}
            >
              <option>RTSP</option>
              <option>RTSPS</option>
              <option>HTTP</option>
            </Input>
          </Col>
        </Row>

        <Row className="align-items-end mt-2">
          <Col md={2}>
            <Label for={fieldId("rtsp_port")}>RTSP port</Label>
            <Input
              id={fieldId("rtsp_port")}
              type="number"
              bsSize="sm"
              value={form.rtsp_port}
              onChange={set("rtsp_port")}
            />
          </Col>
          <Col md={2}>
            <Label for={fieldId("api_port")}>HTTP port</Label>
            <Input
              id={fieldId("api_port")}
              type="number"
              bsSize="sm"
              value={form.api_port}
              onChange={set("api_port")}
            />
          </Col>
          <Col md={2}>
            <Label for={fieldId("username")}>Username</Label>
            <Input id={fieldId("username")} bsSize="sm" value={form.username} onChange={set("username")} />
          </Col>
          <Col md={2}>
            <Label for={fieldId("password")}>Password</Label>
            <Input
              id={fieldId("password")}
              type="password"
              bsSize="sm"
              value={form.password}
              onChange={set("password")}
            />
          </Col>
          <Col md={2}>
            <Label for={fieldId("machine")}>Inference machine</Label>
            {/* Defaults to what the store's cameras resolve to; typed only when they resolve nothing,
                or to probe through another machine than they use. */}
            <InferenceMachineAutocomplete
              value={pickedMachine.id}
              onChange={(id, hostname) => {
                // Same as any connection edit: a frame obtained through the previous machine must not
                // come back and authorize a creation bound to this one.
                runToken.current += 1;
                setProbes({});
                setProbing(false);
                setPickedMachine({ id, hostname: hostname || null });
              }}
            />
            {/* The empty field reads as "Not assigned", but an empty pick falls back to the store's
                machine — say so, and say which one, or the probe looks like it routes nowhere. */}
            {pickedMachine.id == null && resolvedServer && (
              <small className="text-muted d-block" style={{ fontSize: "0.7rem" }}>
                Defaults to <strong>{resolvedServer}</strong> — probes route through it and created cameras are assigned
                to it.
              </small>
            )}
          </Col>
          <Col md={2}>
            <Label for={fieldId("rtsp_url_suffix")}>RTSP URL suffix</Label>
            {/* Probed with, then persisted on the created camera. Without it an IP camera is probed as
                a bare rtsp://host:port and answers nothing. */}
            <Input
              id={fieldId("rtsp_url_suffix")}
              bsSize="sm"
              value={form.rtsp_url_suffix}
              invalid={Boolean(exclusionError)}
              onChange={set("rtsp_url_suffix")}
              placeholder="/Streaming/Channels/1"
            />
          </Col>
        </Row>

        <Row className="align-items-end mt-2">
          <Col md={2}>
            <Label for={fieldId("protocol")}>Protocol</Label>
            <Input
              id={fieldId("protocol")}
              type="select"
              bsSize="sm"
              className="custom-select"
              value={form.protocol}
              onChange={set("protocol")}
            >
              <option>TCP</option>
              <option>UDP</option>
            </Input>
          </Col>
          <Col md={2}>
            <Label for={fieldId("stream_quality")}>Stream quality</Label>
            <Input
              id={fieldId("stream_quality")}
              type="select"
              bsSize="sm"
              className="custom-select"
              value={form.stream_quality}
              onChange={set("stream_quality")}
            >
              <option>SUB</option>
              <option>MAIN</option>
            </Input>
          </Col>
          <Col md={3}>
            <Label for={fieldId("rtsp_port_redirected")}>Recorder RTSP port</Label>
            <Input
              id={fieldId("rtsp_port_redirected")}
              type="number"
              bsSize="sm"
              value={form.rtsp_port_redirected}
              onChange={set("rtsp_port_redirected")}
            />
          </Col>
          <Col md={3}>
            <Label for={fieldId("api_port_redirected")}>Recorder HTTP port</Label>
            <Input
              id={fieldId("api_port_redirected")}
              type="number"
              bsSize="sm"
              value={form.api_port_redirected}
              onChange={set("api_port_redirected")}
            />
          </Col>
        </Row>

        {rangeError && <p className="text-danger mt-2 mb-0">{rangeError}</p>}
        {capError && <p className="text-danger mt-2 mb-0">{capError}</p>}
        {exclusionError && <p className="text-danger mt-2 mb-0">{exclusionError}</p>}
        {nameBraceError && <p className="text-danger mt-2 mb-0">{nameBraceError}</p>}
        {storeError && <p className="text-danger mt-2 mb-0">{storeError}</p>}
        {brandError && <p className="text-danger mt-2 mb-0">{brandError}</p>}
        {serverReason && <p className="text-danger mt-2 mb-0">{serverReason}</p>}
        {suffixError && candidates.length > 0 && <p className="text-danger mt-2 mb-0">{suffixError}</p>}
        {missingFields.length > 0 && (
          <p className="text-warning mt-2 mb-0">A probe needs a {missingFields.join(", ")}.</p>
        )}
        <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.75rem" }}>
          The protocol is saved on the created cameras but not tested by the probe.
        </p>

        {candidates.length > 0 && (
          <p className="text-muted mt-2 mb-0">
            {candidates.length} host{candidates.length > 1 ? "s" : ""} to probe
            {skipped > 0 && `, ${skipped} already in this store`}
            {answered > 0 && ` · ${answered} answered`}
            {silent > 0 && ` · ${silent} silent`}
            {inFlight > 0 && ` · ${inFlight} in flight`}
          </p>
        )}

        {candidates.length > 0 && (
          <Button className="btn-info mt-2" disabled={!canProbe} onClick={runProbes}>
            {probing ? (
              <ThreeDots height="15" width="20" radius="5" color="#ffffff" ariaLabel="loading" visible />
            ) : (
              <i className="fa fa-binoculars mr-2" />
            )}
            Probe {candidates.length} host{candidates.length === 1 ? "" : "s"}
          </Button>
        )}
        {probing && (
          <Button className="ml-2 mt-2" outline color="secondary" onClick={stopProbes}>
            Stop
          </Button>
        )}

        {failure && (
          <Alert color="danger" className="mt-3">
            {failure}
          </Alert>
        )}

        {created > 0 && (
          <Alert color="success" className="mt-3">
            {created} capture device{created === 1 ? "" : "s"} created, each with its camera on slot 1. Change the host
            range to carry on — the connection fields are kept.
          </Alert>
        )}

        {/* The grid doubles as the range preview: the literal hosts are on screen before anything is
            probed, so a mistyped range is caught before it costs a capture. */}
        {rows.length > 0 && (
          <>
            <div className="d-flex flex-wrap mt-3">
              {rows.map((row) => (
                <div key={row.host} className="mr-3 mb-3 text-center" style={{ width: 170 }}>
                  {row.url ? (
                    <ProbeFrame url={row.url} operationId={row.operationId} alt={row.host} />
                  ) : (
                    <>
                      <div
                        id={tileId(row.host)}
                        style={{
                          height: 96,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          background: "rgba(255,255,255,0.06)",
                          borderRadius: 4,
                          fontSize: "0.75rem",
                          padding: 6,
                          overflow: "hidden",
                          cursor: statusDetail(row) ? "help" : "default",
                        }}
                      >
                        <span>{statusLabel(row)}</span>
                        {statusDetail(row) && <span className="text-muted mt-1">why?</span>}
                      </div>
                      {/* The reason only shows on hover, to keep the tile in the grid. */}
                      {statusDetail(row) && (
                        <UncontrolledTooltip
                          target={tileId(row.host)}
                          placement="top"
                          style={{ maxWidth: 420, textAlign: "left", wordBreak: "break-word" }}
                        >
                          {statusDetail(row)}
                        </UncontrolledTooltip>
                      )}
                    </>
                  )}
                  <div className="mt-1">
                    {row.status === "hit" && (
                      <Input
                        type="checkbox"
                        id={fieldId(`host-${row.host}`)}
                        className="position-static m-0 mr-2 d-inline-block"
                        aria-label={row.host}
                        checked={Boolean(row.kept)}
                        onChange={() => toggle(row.host)}
                      />
                    )}
                    <span>{row.host}</span>
                    {(row.status === "failed" || row.status === "stopped") && (
                      <Button
                        size="sm"
                        outline
                        color="secondary"
                        className="ml-2"
                        disabled={probing || creating}
                        onClick={() => probeHosts([row.host])}
                      >
                        Retry
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {keptRows.length > 0 && (
              <Button className="btn-info" disabled={creating} onClick={handleCreate}>
                {creating ? (
                  <ThreeDots height="15" width="20" radius="5" color="#ffffff" ariaLabel="loading" visible />
                ) : (
                  <i className="fa fa-plus mr-2" />
                )}
                Create {keptRows.length} capture device{keptRows.length === 1 ? "" : "s"}
              </Button>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

BulkCaptureDeviceForm.propTypes = {
  /** Null while the parent is still resolving it — probing is refused until it lands. */
  storeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Recorder brands, for the shared brand select; the chosen kind is the probe's `brand_kind`. */
  brands: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, kind: PropTypes.string })),
  /** The store's capture devices: the probe vehicle, and the hosts to skip. */
  tree: PropTypes.arrayOf(PropTypes.shape({ public_id: PropTypes.string, host: PropTypes.string })),
  /** Inference-machine id → hostname, for routing the probe. */
  machineCache: PropTypes.objectOf(PropTypes.string),
  /** Called with `{publicIds, created}` once the batch landed, so the host can refetch the tree. */
  onCreated: PropTypes.func,
  onClose: PropTypes.func,
};

BulkCaptureDeviceForm.defaultProps = {
  storeId: null,
  brands: [],
  tree: [],
  machineCache: {},
  onCreated: undefined,
  onClose: undefined,
};
